import { useEffect, useMemo } from 'react';
import { DEFAULT_POLL_INTERVAL } from 'shared/constants';
import { GraphQLFetchPolicies } from 'shared/enums';
import {
  BoardFilterView,
  ChannelStatusEntity,
  DateRangeOutput,
  GetChannelStatusIdsWithEntityCountOfZeroWhere,
  GetEmptyChannelStatusIdsQuery,
  GetPaginatedChannelStatusesQuery,
  SubscribeToClinicUserChannelsDocument,
  useGetEmptyChannelStatusIdsQuery,
  useGetPaginatedChannelStatusesQuery,
} from 'shared/types/graphql';
import debounce from 'lodash-es/debounce';

interface UseGetPaginatedChannelStatusesProps {
  clinicId: string;
  selectedChannelStatusIds?: string[];
  columnsPerPage?: number;
  channelStatusPageNumber: number;
  currentView?: BoardFilterView | null;
  dateRange?: DateRangeOutput | null;
}

interface UseGetPaginatedChannelStatusesReturn {
  visibleChannelStatuses?: GetPaginatedChannelStatusesQuery['channelStatuses'];
  hasNextStatusPage?: boolean;
  isLoadingChannelStatusData: boolean;
  loadedOnce: boolean;
  emptyChannelStatusIds?: GetEmptyChannelStatusIdsQuery['getChannelStatusIdsWithEntityCountOfZero'];
}

export const DEFAULT_COLUMNS_PER_PAGE = 4;

export function useGetPaginatedChannelStatuses({
  clinicId,
  selectedChannelStatusIds,
  channelStatusPageNumber,
  currentView,
  dateRange,
  columnsPerPage = DEFAULT_COLUMNS_PER_PAGE,
}: UseGetPaginatedChannelStatusesProps): UseGetPaginatedChannelStatusesReturn {
  const {
    data: channelStatusesData,
    loading: isLoadingChannelStatusData,
    called: loadedOnce,
  } = useGetPaginatedChannelStatusesQuery({
    fetchPolicy: GraphQLFetchPolicies.CacheFirst,
    variables: {
      where: {
        clinicSetting: { clinic: { id: { equals: clinicId } } },
        deletedOn: { equals: null },
        ...(!!selectedChannelStatusIds?.length && { id: { in: selectedChannelStatusIds } }),
      },
    },
    pollInterval: DEFAULT_POLL_INTERVAL,
    skip: !clinicId,
  });

  const queryVarsWhere: GetChannelStatusIdsWithEntityCountOfZeroWhere = useMemo(
    () => ({
      clinicId,
      entity:
        currentView === BoardFilterView.Appointments ? ChannelStatusEntity.Appointment : ChannelStatusEntity.Channel,
      start: dateRange?.gte,
      end: dateRange?.lte,
      channelStatusIds: channelStatusesData?.channelStatuses?.map(({ id }) => id) || [],
    }),
    [channelStatusesData?.channelStatuses, clinicId, currentView, dateRange?.gte, dateRange?.lte],
  );

  const {
    data: emptyChannelStatusIdsData,
    loading: isLoadingEmptyChannelStatusIds,
    subscribeToMore: subscribeToChannels,
    refetch: refetchEmptyChannelStatusIds,
  } = useGetEmptyChannelStatusIdsQuery({
    variables: {
      where: queryVarsWhere,
    },
    skip: !clinicId || !channelStatusesData?.channelStatuses?.length || !queryVarsWhere.start || !queryVarsWhere.end,
  });

  const visibleChannelStatuses = useMemo(
    () => channelStatusesData?.channelStatuses?.slice(0, channelStatusPageNumber * columnsPerPage),
    [channelStatusesData?.channelStatuses, channelStatusPageNumber, columnsPerPage],
  );

  const emptyChannelStatusIds = useMemo(() => {
    return emptyChannelStatusIdsData?.getChannelStatusIdsWithEntityCountOfZero;
  }, [emptyChannelStatusIdsData]);

  const hasNextStatusPage = useMemo(() => {
    return (
      channelStatusesData?.count !== 0 && (channelStatusesData?.count || 0) > channelStatusPageNumber * columnsPerPage
    );
  }, [channelStatusPageNumber, channelStatusesData, columnsPerPage]);

  const isLoading = useMemo(
    () => isLoadingChannelStatusData || isLoadingEmptyChannelStatusIds,
    [isLoadingEmptyChannelStatusIds, isLoadingChannelStatusData],
  );

  // Subscribe to channel changes to ensure newly relevant channel statuses are displayed if channels become associated with them
  subscribeToChannels({
    document: SubscribeToClinicUserChannelsDocument,
    variables: { clinicId },
    updateQuery: (prev) => {
      //TODO Temporary workaround: Remove this condition after upgrading Apollo to a version that handles skip correctly for refetch calls
      if (queryVarsWhere?.start && queryVarsWhere.end) {
        // Prevent unnecessary refetches by debouncing
        debounce(() => {
          refetchEmptyChannelStatusIds({
            where: queryVarsWhere,
          });
        }, 100)();
      }
      return prev;
    },
  });

  useEffect(() => {
    if (!isLoadingChannelStatusData && queryVarsWhere?.start && queryVarsWhere.end) {
      refetchEmptyChannelStatusIds({
        where: queryVarsWhere,
      });
    }
    // eslint-disable-next-line
  }, [isLoadingChannelStatusData]);

  return {
    visibleChannelStatuses,
    hasNextStatusPage,
    isLoadingChannelStatusData: isLoading,
    loadedOnce,
    emptyChannelStatusIds,
  };
}
